import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ParamField, ParamValues } from '@barev/core';
import { Button, Card, Center, Chip, Field, Icon, Spinner, Stepper } from '@barev/ui';
import { tOptional, dateKeyToDate, errorText, haptic, navigate, nextDays, useI18n, useLocation, useToast, useTRPC } from '@barev/web';
import { useEffect, useMemo, useState } from 'react';
import { PageHead } from '../lib.tsx';

/** ORD-2…5: параметры → дата и время → адрес → итог со скидкой → подтверждение */
export function OrderForm({ code }: { code: string }) {
  const { t, amd, dt, locale } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const { query } = useLocation();
  const repeatId = query.get('repeat');

  const service = useQuery(trpc.client.service.queryOptions({ code }));
  const me = useQuery(trpc.client.me.queryOptions());
  const districts = useQuery(trpc.client.districts.queryOptions());
  const methods = useQuery(trpc.client.paymentMethods.queryOptions());
  const repeat = useQuery({ ...trpc.client.orders.repeat.queryOptions({ id: repeatId ?? '' }), enabled: !!repeatId });

  const [params, setParams] = useState<ParamValues | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<Date | null>(null);
  const [districtId, setDistrictId] = useState('');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [methodId, setMethodId] = useState('');
  // промокод: что ввёл клиент и что применено к расчёту
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  // барики: списать в этом заказе (с промокодом нельзя)
  const [usePts, setUsePts] = useState(false);
  const [touched, setTouched] = useState(false);

  // дефолты услуги, затем — параметры повторяемого заказа (ORD-7)
  useEffect(() => {
    if (service.data && !params) setParams(service.data.defaults);
  }, [service.data, params]);
  useEffect(() => {
    if (!repeat.data) return;
    setParams((p) => ({ ...(p ?? {}), ...(repeat.data.params as ParamValues) }));
    setAddress(repeat.data.addressText ?? '');
    setDistrictId(repeat.data.districtId ?? '');
    setNote(repeat.data.note ?? '');
  }, [repeat.data]);
  useEffect(() => {
    if (methods.data?.[0] && !methodId) setMethodId(methods.data[0].id);
  }, [methods.data, methodId]);

  const days = useMemo(() => nextDays(service.data?.horizonDays ?? 14), [service.data?.horizonDays]);
  useEffect(() => {
    if (!day && days[0]) setDay(days[0]);
  }, [days, day]);

  const quote = useQuery({
    ...trpc.client.quote.queryOptions({ serviceCode: code, params: params ?? {}, promoCode: promo || undefined }),
    enabled: !!params,
    placeholderData: keepPreviousData,
    retry: false,
  });
  // неподходящий код — показываем причину и считаем без него
  useEffect(() => {
    if (promo && quote.error) {
      setPromoError(errorText(t, quote.error));
      setPromo('');
    }
  }, [promo, quote.error, t]);
  const slots = useQuery({
    ...trpc.client.slots.queryOptions({ serviceCode: code, params: params ?? {}, dateKey: day ?? '' }),
    enabled: !!params && !!day,
  });
  // смена параметров меняет длительность — выбранное время могло стать недоступным
  useEffect(() => {
    if (slot && slots.data && !slots.data.some((s) => s.start.getTime() === slot.getTime())) setSlot(null);
  }, [slots.data, slot]);

  const create = useMutation(
    trpc.client.createOrder.mutationOptions({
      onSuccess: (r) => {
        // заказ уже создан: сначала переходим к нему, остальное — не критично
        navigate(`/orders/${r.id}`, { replace: true });
        toast(t('client.order.created', { number: r.number }));
        void qc.invalidateQueries();
        haptic.ok();
      },
      onError: (e) => {
        haptic.error();
        toast(errorText(t, e), 'error');
        void slots.refetch();
      },
    }),
  );

  if (service.isPending || !params)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  if (!service.data?.active) {
    return (
      <main className="app app--plain">
        <PageHead title={t(`services.${code}.name`)} />
        <Card>
          <p style={{ margin: 0 }}>{t('client.order.unavailable')}</p>
        </Card>
      </main>
    );
  }

  const s = service.data;
  const set = (key: string, v: string | number | boolean) => setParams({ ...params, [key]: v });
  const needsAddress = !s.isOnline;
  const missing = {
    slot: !slot,
    district: needsAddress && !districtId,
    address: needsAddress && address.trim().length < 3,
  };
  const valid = !missing.slot && !missing.district && !missing.address;

  function submit() {
    setTouched(true);
    if (!valid || !slot) return;
    create.mutate({
      serviceCode: code,
      params: params!,
      scheduledAt: slot,
      addressText: needsAddress ? address.trim() : undefined,
      districtId: needsAddress ? districtId : undefined,
      note: note.trim() || undefined,
      paymentMethodId: methodId || undefined,
      repeatOfId: repeatId ?? undefined,
      promoCode: q?.promo ? promo : undefined,
      usePoints: ptsUsed || undefined,
    });
  }

  const q = quote.data;
  const ptsAvailable = !!q?.points.allowed && !q.promo;
  const ptsUsed = usePts && ptsAvailable ? q!.points.max : 0;
  return (
    <main className="app app--plain">
      <PageHead title={t(`services.${code}.name`)} />
      <p className="bc-muted" style={{ margin: '-4px 0 0' }}>
        {tOptional(t, `services.${code}.description`)}
      </p>

      {/* параметры услуги */}
      <Card className="bc-stack" style={{ gap: 16 }}>
        {s.paramsSchema.fields
          .filter((f) => f.type !== 'extras')
          .map((f) => (
            <ParamInput key={f.key} serviceCode={code} field={f} value={params[f.key]} onChange={(v) => set(f.key, v)} prices={f.key === 'cleaningType' ? s.variantPrices : undefined} />
          ))}
        {q && (
          <div className="bc-row bc-small bc-muted">
            <Icon name="clock" size={16} />
            {t('client.order.duration', { hours: (q.durationMinutes / 60).toLocaleString(locale, { maximumFractionDigits: 1 }) })}
          </div>
        )}
      </Card>

      {/* доп. услуги — клиент сам выбирает, что сделать (модель Qlean) */}
      {s.paramsSchema.fields.map((f) =>
        f.type === 'extras' ? (
          <ExtrasPicker
            key={f.key}
            items={f.items}
            prices={s.extrasPrices}
            value={(params[f.key] as Record<string, number> | undefined) ?? {}}
            onChange={(v) => setParams({ ...params, [f.key]: v })}
          />
        ) : null,
      )}

      {/* ORD-3: дата и время */}
      <Card className="bc-stack">
        <h2 className="bc-h3">{t('client.order.when')}</h2>
        <div className="days" role="listbox" aria-label={t('client.order.date')}>
          {days.map((d) => {
            const date = dateKeyToDate(d);
            return (
              <button
                key={d}
                type="button"
                className="day"
                aria-pressed={d === day}
                onClick={() => {
                  setDay(d);
                  setSlot(null);
                }}
              >
                <span className="bc-small">{dt(date, { weekday: 'short' })}</span>
                <span className="day__num">{dt(date, { day: 'numeric' })}</span>
              </button>
            );
          })}
        </div>
        {slots.isPending ? (
          <Spinner />
        ) : slots.data && slots.data.length > 0 ? (
          <div className="slots">
            {slots.data.map((sl) => (
              <button key={sl.start.toISOString()} type="button" className="slot" aria-pressed={slot?.getTime() === sl.start.getTime()} onClick={() => setSlot(sl.start)}>
                {dt(sl.start, { hour: '2-digit', minute: '2-digit' })}
              </button>
            ))}
          </div>
        ) : (
          <p className="bc-muted" style={{ margin: 0 }}>
            {t('client.order.noSlots')}
          </p>
        )}
        {touched && missing.slot && <div className="bc-error">{t('client.order.pickSlot')}</div>}
      </Card>

      {/* адрес */}
      {needsAddress && (
        <Card className="bc-stack">
          <h2 className="bc-h3">{t('client.order.address')}</h2>
          <Field label={t('client.order.district')} htmlFor="district" error={touched && missing.district ? t('client.order.pickDistrict') : undefined}>
            <select id="district" className="bc-input" value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
              <option value="">{t('client.order.districtPlaceholder')}</option>
              {districts.data?.map((d) => (
                <option key={d.id} value={d.id}>
                  {t(`districts.${d.code}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('client.order.addressLabel')} htmlFor="address" error={touched && missing.address ? t('client.order.enterAddress') : undefined}>
            <input id="address" className="bc-input" value={address} maxLength={300} placeholder={t('client.order.addressPlaceholder')} onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
          </Field>
          <Field label={t('client.order.note')} htmlFor="note">
            <textarea id="note" className="bc-input" value={note} maxLength={1000} placeholder={t('client.order.notePlaceholder')} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Card>
      )}

      {/* PAY-1: способ оплаты, оплата отмечается вручную */}
      {methods.data && methods.data.length > 0 && (
        <Card className="bc-stack">
          <h2 className="bc-h3">{t('client.order.payment')}</h2>
          <div className="bc-chips">
            {methods.data.map((m) => (
              <Chip key={m.id} pressed={m.id === methodId} onClick={() => setMethodId(m.id)}>
                {(m.name as Record<string, string>)[locale] || (m.name as Record<string, string>).ru}
              </Chip>
            ))}
          </div>
          <p className="bc-small bc-muted" style={{ margin: 0 }}>
            {t('client.order.paymentHint')}
          </p>
        </Card>
      )}

      {/* ORD-4: Стоимость / Скидка Barev+ / Итого — разбивка */}
      <Card className="bc-stack">
        {q ? (
          <div className="price-lines">
            {q.extras.length > 0 && (
              <>
                <div className="bc-between">
                  <span>{t(`services.${code}.name`)}</span>
                  <span>{amd(q.servicePrice)}</span>
                </div>
                {q.extras.map((l) => (
                  <div key={l.code} className="bc-between bc-small">
                    <span>
                      {t(`extras.${l.code}.name`)}
                      {l.qty > 1 ? ` × ${l.qty}` : ''}
                    </span>
                    <span>{amd(l.amount)}</span>
                  </div>
                ))}
                <hr className="bc-divider" />
              </>
            )}
            <div className="bc-between">
              <span>{t('client.order.price')}</span>
              <span>{amd(q.basePrice)}</span>
            </div>
            {q.discountAmount > 0 ? (
              <div className="bc-between" style={{ color: 'var(--bc-accent)', fontWeight: 600 }}>
                <span>{q.promo ? t('client.order.promoLine', { code: q.promo.code, pct: q.discountPct }) : t('client.order.discount', { pct: q.discountPct })}</span>
                <span>−{amd(q.discountAmount)}</span>
              </div>
            ) : (
              s.categoryDiscountPct > 0 && (
                <a href="#/membership" className="bc-small" style={{ color: 'var(--bc-accent)', fontWeight: 600 }}>
                  {t('client.order.plusHint', { pct: s.categoryDiscountPct })}
                </a>
              )
            )}
          </div>
        ) : (
          <Spinner />
        )}
        {q && q.points.balance > 0 && (
          <label className="points-toggle" data-off={!ptsAvailable || undefined}>
            <input type="checkbox" checked={usePts && ptsAvailable} disabled={!ptsAvailable} onChange={(e) => setUsePts(e.target.checked)} />
            <span>
              <b>{t('client.points.useInOrder', { amount: amd(q.points.max).replace(/\s?֏$/, '') })}</b>
              <small>
                {q.promo
                  ? t('client.points.notWithPromo')
                  : !q.points.allowed
                    ? t('client.points.belowMin', { min: amd(q.points.min).replace(/\s?֏$/, ''), balance: amd(q.points.balance).replace(/\s?֏$/, '') })
                    : t('client.points.useHint', { pct: q.points.sharePct, balance: amd(q.points.balance).replace(/\s?֏$/, '') })}
              </small>
            </span>
          </label>
        )}
        {ptsUsed > 0 && (
          <div className="bc-between" style={{ color: 'var(--bc-accent)', fontWeight: 600 }}>
            <span>{t('client.points.paidLine')}</span>
            <span>−{amd(ptsUsed)}</span>
          </div>
        )}
        <PromoField
          value={promoInput}
          onChange={(v) => {
            setPromoInput(v);
            setPromoError(null);
          }}
          applied={promo}
          active={!!q?.promo}
          error={promoError}
          loading={quote.isFetching}
          onApply={() => {
            setPromoError(null);
            setPromo(promoInput.trim());
          }}
          onRemove={() => {
            setPromo('');
            setPromoInput('');
          }}
        />
        <p className="bc-small bc-muted" style={{ margin: 0 }}>
          {t('client.order.cancelPolicy', { hours: me.data?.freeCancelHours ?? 24, pct: s.lateCancelFeePct })}
        </p>
      </Card>

      {/* закреплённый низ: только итог, время и кнопка — не закрывает форму на телефоне */}
      <Card className="bc-stack sticky-cta" style={{ boxShadow: 'var(--bc-shadow)', gap: 10 }}>
        <div className="bc-between">
          <span className="bc-num" style={{ fontSize: 22 }}>
            {t('client.order.total')}
          </span>
          <span className="bc-num" style={{ fontSize: 22 }}>
            {q ? amd(q.totalPrice - ptsUsed) : '…'}
          </span>
        </div>
        {slot && (
          <div className="bc-small bc-muted" style={{ marginTop: -6 }}>
            {dt(slot, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        <Button block onClick={submit} disabled={create.isPending}>
          {t('client.order.confirm')}
        </Button>
      </Card>
    </main>
  );
}

function ParamInput({
  serviceCode,
  field,
  value,
  onChange,
  prices,
}: {
  serviceCode: string;
  field: ParamField;
  value: unknown;
  onChange: (v: string | number | boolean) => void;
  prices?: Record<string, number>;
}) {
  if (field.type === 'extras') return null;
  const { t, amd } = useI18n();
  const label = t(`params.${serviceCode}.${field.key}.label`);
  switch (field.type) {
    case 'select':
      return (
        <div className="bc-field">
          <span className="bc-label">{label}</span>
          <div className="option-grid" role="radiogroup" aria-label={label}>
            {field.options.map((o) => (
              <button key={o} type="button" className="option" role="radio" aria-checked={value === o} aria-pressed={value === o} onClick={() => onChange(o)}>
                <span style={{ fontWeight: 700 }}>{t(`params.${serviceCode}.${field.key}.${o}`)}</span>
                <span className="bc-small bc-muted">
                  {prices?.[o] !== undefined ? t('common.from', { price: amd(prices[o]!) }) : tOptional(t, `params.${serviceCode}.${field.key}.${o}_hint`)}
                </span>
              </button>
            ))}
          </div>
        </div>
      );
    case 'int':
      return (
        <div className="bc-between">
          <span style={{ fontWeight: 600 }}>{label}</span>
          <Stepper value={Number(value ?? field.min)} min={field.min} max={field.max} onChange={onChange} decLabel={t('common.decrease')} incLabel={t('common.increase')} />
        </div>
      );
    case 'text':
      return (
        <Field label={label}>
          <textarea className="bc-input" value={String(value ?? '')} maxLength={field.maxLength} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
    case 'bool':
      return (
        <Chip pressed={value === true} onClick={() => onChange(value !== true)}>
          {label}
        </Chip>
      );
  }
}

/** Доп. услуги: «за окно», «за час»… — счётчик; разовые услуги — переключатель */
function ExtrasPicker({
  items,
  prices,
  value,
  onChange,
}: {
  items: { code: string; max: number }[];
  prices: Record<string, { price: number; unit: string }>;
  value: Record<string, number>;
  onChange: (v: Record<string, number>) => void;
}) {
  const { t, amd } = useI18n();
  const set = (code: string, qty: number) => {
    const next = { ...value };
    if (qty > 0) next[code] = qty;
    else delete next[code];
    onChange(next);
  };
  return (
    <Card className="bc-stack" style={{ gap: 4 }}>
      <h2 className="bc-h3" style={{ marginBottom: 6 }}>
        {t('client.order.extras')}
      </h2>
      {items.map((it) => {
        const p = prices[it.code];
        if (!p) return null;
        const qty = value[it.code] ?? 0;
        return (
          <div key={it.code} className="extra-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{t(`extras.${it.code}.name`)}</div>
              <div className="bc-small bc-muted">
                {amd(p.price)}
                {p.unit !== 'service' ? ` ${t(`extras.${it.code}.per`)}` : ''}
              </div>
            </div>
            {it.max === 1 ? (
              <button type="button" className="extra-toggle" aria-pressed={qty > 0} onClick={() => set(it.code, qty > 0 ? 0 : 1)}>
                {qty > 0 ? <Icon name="check" size={18} strokeWidth={2.6} /> : t('client.order.add')}
              </button>
            ) : (
              <Stepper value={qty} min={0} max={it.max} onChange={(n) => set(it.code, n)} decLabel={t('common.decrease')} incLabel={t('common.increase')} />
            )}
          </div>
        );
      })}
    </Card>
  );
}

/** Промокод: применённый — плашкой с кнопкой «Убрать»; если скидка подписки больше — так и пишем */
function PromoField(p: { value: string; onChange: (v: string) => void; applied: string; active: boolean; error: string | null; loading: boolean; onApply: () => void; onRemove: () => void }) {
  const { t } = useI18n();
  if (p.applied && !p.loading)
    return (
      <div className="promo-applied">
        <span>
          <b>{p.applied.toUpperCase()}</b>
          {!p.active && <span className="bc-small">{t('client.order.promoWeaker')}</span>}
        </span>
        <button type="button" onClick={p.onRemove}>
          {t('client.order.promoRemove')}
        </button>
      </div>
    );
  return (
    <form
      className="promo-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (p.value.trim().length >= 2) p.onApply();
      }}
    >
      <input className="bc-input" value={p.value} maxLength={40} autoCapitalize="characters" placeholder={t('client.order.promoPlaceholder')} aria-label={t('client.order.promo')} onChange={(e) => p.onChange(e.target.value.toUpperCase())} />
      <button type="submit" className="pill pill--dark" disabled={p.value.trim().length < 2 || p.loading}>
        {t('client.order.promoApply')}
      </button>
      {p.error && <div className="bc-error promo-form__error">{p.error}</div>}
    </form>
  );
}
